import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { HeaderTitle } from './header-title/header-title.model';
import { HeaderTitleService } from './header-title/header-title.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class TabTitleService {
    private readonly appName = 'In Time PRO';
    private readonly defaultTitle = 'In Time PRO';

    private routeTitle = '';
    private breadcrumbTitle = '';
    private subscriptions: Subscription[] = [];

    constructor(
        private router: Router,
        private titleService: Title,
        private headerTitleService: HeaderTitleService
    ) {
    }

    init(): void {
        if (this.subscriptions.length > 0) {
            return;
        }

        this.subscriptions.push(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => {
                    this.breadcrumbTitle = '';
                    this.routeTitle = this.resolveRouteTitle();
                    this.applyTitle();
                })
        );

        this.subscriptions.push(
            this.headerTitleService.getPageTitle().subscribe((headerTitle: HeaderTitle) => {
                this.breadcrumbTitle = this.resolveBreadcrumbTitle(headerTitle);
                this.applyTitle();
            })
        );

        this.routeTitle = this.resolveRouteTitle();
        this.applyTitle();
    }

    private resolveBreadcrumbTitle(headerTitle: HeaderTitle): string {
        if (!headerTitle || !headerTitle.breadcrumbs || headerTitle.breadcrumbs.length === 0) {
            return '';
        }

        const tokens = headerTitle.breadcrumbs
            .map(b => (b && b.text ? this.normalizeTitleToken(b.text) : ''))
            .filter(t => !!t);

        return tokens.join(' | ');
    }

    private resolveRouteTitle(): string {
        const routeDataTitle = this.resolveRouteDataTitle(this.router.routerState.snapshot.root);
        if (routeDataTitle) {
            return this.normalizeTitleToken(routeDataTitle);
        }

        const pathWithoutQuery = (this.router.url || '').split('?')[0].split('#')[0];
        const segments = pathWithoutQuery.split('/').filter((segment) => !!segment);

        for (let i = segments.length - 1; i >= 0; i--) {
            const segment = segments[i];

            if (this.isIgnoredSegment(segment) || this.isLikelyRouteParam(segment)) {
                continue;
            }

            return this.normalizeTitleToken(segment);
        }

        return '';
    }

    private resolveRouteDataTitle(snapshot: ActivatedRouteSnapshot): string {
        if (!snapshot) {
            return '';
        }

        if (snapshot.data && snapshot.data.title) {
            return snapshot.data.title;
        }

        for (const child of snapshot.children) {
            const childTitle = this.resolveRouteDataTitle(child);
            if (childTitle) {
                return childTitle;
            }
        }

        return '';
    }

    private applyTitle(): void {
        const pageTitle = this.breadcrumbTitle || this.routeTitle;

        if (pageTitle) {
            this.titleService.setTitle(pageTitle + ' | ' + this.appName);
            return;
        }

        this.titleService.setTitle(this.defaultTitle);
    }

    private isIgnoredSegment(segment: string): boolean {
        const normalizedSegment = segment.toLowerCase();
        return normalizedSegment === 'app'
            || normalizedSegment === 'main'
            || normalizedSegment === 'admin'
            || normalizedSegment === 'account';
    }

    private isLikelyRouteParam(segment: string): boolean {
        if (!segment) {
            return true;
        }

        if (/^\d+$/.test(segment)) {
            return true;
        }

        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)) {
            return true;
        }

        if (/^[a-f0-9]{24}$/i.test(segment)) {
            return true;
        }

        return false;
    }

    private normalizeTitleToken(value: string): string {
        if (!value) {
            return '';
        }

        const decodedValue = decodeURIComponent(value);
        const spacedValue = decodedValue
            .replace(/[-_]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim();

        if (!spacedValue) {
            return '';
        }

        return spacedValue
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
